import { actionFlag, worstMarginDeparture } from './marginBands';
import { inReview, queueCodes, reviewOf, statusOf } from './reviewLogic';
import { CommandDeparture, CommandFlag, ConfidenceTier, CostBasis, Severity } from './types';

export const counted = (d: CommandDeparture): boolean => (d.on_sale ?? d.state === 'On sale') || d.pax > 0;
export const basisValue = (
  d: CommandDeparture,
  field: 'cost' | 'le_margin' | 'margin_pct' | 'pred_margin' | 'pred_margin_pct' | 'flag' | 'rec',
  basis: CostBasis,
) => {
  return d[`${field}_${basis}`];
};

export function agg(rows: Array<CommandDeparture>, basis: CostBasis) {
  const c = rows.filter(counted);
  const pax = c.reduce((s, d) => s + d.pax, 0);
  const cap = c.reduce((s, d) => s + (d.max_pax || 0), 0);
  const ppax = c.reduce((s, d) => s + (d.pred_pax || 0), 0);
  const ttv = rows.reduce((s, d) => s + (d.ttv || 0), 0);
  const marginRows = rows.filter(
    (d) => d[`margin_bands_${basis}`]?.margin_available && d[`margin_pct_${basis}`] != null,
  );
  const predMarginRows = rows.filter(
    (d) => d[`margin_bands_${basis}`]?.margin_available && d[`pred_margin_pct_${basis}`] != null,
  );
  const m = marginRows.length ? marginRows.reduce((s, d) => s + (d[`le_margin_${basis}`] || 0), 0) : null;
  // Cost and margin add up over the same departures: those whose margin is known. TTV covers every departure.
  const marginTtv = marginRows.reduce((s, d) => s + (d.ttv || 0), 0);
  const marginKept = marginRows.reduce((s, d) => s + (d.kept_from_cancellations || 0), 0);
  const supplier = marginRows.reduce((s, d) => s + (d[`cost_${basis}`] || 0), 0);
  const pttv = rows.reduce((s, d) => s + (d.pred_ttv || 0), 0);
  const pmv = predMarginRows.length ? predMarginRows.reduce((s, d) => s + (d[`pred_margin_${basis}`] || 0), 0) : null;
  const predMarginTtv = predMarginRows.reduce((s, d) => s + (d.pred_ttv || 0), 0);
  const predMarginKept = predMarginRows.reduce((s, d) => s + (d.kept_from_cancellations || 0), 0);
  const psupplier = predMarginRows.reduce((s, d) => s + (d[`pred_cost_${basis}`] || 0), 0);
  // Break-Even Load Factor over the dates whose break-even point is known (the others stay out of both sums).
  const known = c.filter((d) => d[`breakeven_pax_${basis}`] != null && d.max_pax > 0);
  const bePax = known.reduce((s, d) => s + (d[`breakeven_pax_${basis}`] || 0), 0);
  const beCap = known.reduce((s, d) => s + d.max_pax, 0);
  const tiers: Array<ConfidenceTier> = ['indicative', 'low', 'medium', 'high'];
  // Handback-day and past-handback numbers are final; confidence describes only the remaining forecasts.
  const confidenceRows = rows.filter((d) => d.days_to_handback > 0);
  const confidencePaxRows = confidenceRows.filter(counted);
  const confidencePax = confidencePaxRows.reduce((sum, d) => sum + (d.pred_pax || 0), 0);
  const confidenceTtv = confidenceRows.reduce((sum, d) => sum + (d.pred_ttv || 0), 0);
  const confidenceMix =
    confidencePax > 0
      ? tiers.map((tier) => {
          const tierPax = confidencePaxRows
            .filter((d) => (d.pred_confidence_tier || 'indicative') === tier)
            .reduce((sum, d) => sum + d.pred_pax, 0);
          const tierTtv = confidenceRows
            .filter((d) => (d.pred_confidence_tier || 'indicative') === tier)
            .reduce((sum, d) => sum + (d.pred_ttv || 0), 0);
          return {
            tier,
            predictedPax: tierPax,
            predictedTtv: tierTtv,
            paxShare: tierPax / confidencePax,
            ttvShare: confidenceTtv > 0 ? tierTtv / confidenceTtv : 0,
          };
        })
      : [];
  const confidenceTier = [...confidenceMix].sort((a, b) => b.predictedPax - a.predictedPax)[0]?.tier ?? null;
  return {
    confidenceMix,
    confidenceTier,
    ...(rows.some((d) => statusOf(d, basis))
      ? {
          urgent: rows.filter((d) => statusOf(d, basis) === 'Urgent').length,
          needsAction: rows.filter((d) => ['Urgent', 'Needs Action'].includes(statusOf(d, basis))).length,
          assessmentIncomplete: rows.filter((d) => statusOf(d, basis) === 'Assessment Incomplete').length,
          reviewWatch: rows.filter((d) => statusOf(d, basis) === 'Watch').length,
        }
      : {}),
    n: rows.length,
    hidden: rows.filter((d) => d.hidden).length,
    onTrack: rows.filter((d) => departureOnTrack(d, basis)).length,
    loss_today: rows.filter((d) => departureSeverity(d, basis) === 'loss_today').length,
    predicted_loss: rows.filter((d) => departureSeverity(d, basis) === 'predicted_loss').length,
    other_action: rows.filter((d) => departureSeverity(d, basis) === 'other_action').length,
    watch: rows.filter((d) => departureSeverity(d, basis) === 'watch').length,
    live: c.length,
    pax,
    bundle_pax: rows.reduce((sum, d) => sum + (d.bundle_pax || 0), 0),
    kept_from_cancellations: rows.reduce((sum, d) => sum + (d.kept_from_cancellations || 0), 0),
    ppax,
    cap,
    lf: cap ? pax / cap : null,
    plf: cap ? ppax / cap : null,
    beLf: beCap ? bePax / beCap : null,
    ttv,
    pttv,
    m,
    // Margin includes Insurance Breakage retained from cancellations; add it back to recover cost.
    // `supplier` / `psupplier` split supplier cost from ICS at cost-plus for tooltips.
    cost: m != null ? marginTtv - m + marginKept : null,
    supplier,
    pcost: pmv != null ? predMarginTtv - pmv + predMarginKept : null,
    psupplier,
    mp: m != null && marginTtv ? m / marginTtv : null,
    pm: pmv != null && predMarginTtv ? pmv / predMarginTtv : null,
    pmv,
    // Margin known for X of Y: departures with bookings (booked) or a predicted TTV (predicted).
    marginKnown: marginRows.length,
    marginOf: rows.filter((d) => (d.ttv || 0) > 0).length,
    predMarginKnown: predMarginRows.length,
    predMarginOf: rows.filter((d) => (d.pred_ttv || 0) > 0).length,
    // Dates past handback: their prediction is what is booked.
    final: c.filter((d) => d.days_to_handback < 0).length,
    red: rows.filter((d) => basisValue(d, 'flag', basis) === 'red').length,
    amber: rows.filter((d) => basisValue(d, 'flag', basis) === 'amber').length,
    empty: rows.filter((d) => basisValue(d, 'flag', basis) === 'empty').length,
    early: rows.filter((d) => basisValue(d, 'flag', basis) === 'early').length,
    closed: rows.filter((d) => d.state === 'Closed (not on sale)').length,
    soldOut: rows.filter((d) => d.state === 'Sold out').length,
  };
}

export interface CommandFilters {
  inReview?: boolean;
  reviewStatus?: string;
  rmReview?: boolean;
  queue?: string | null;
  sellOut?: boolean;
  reviewSort?: 'priority' | 'longest';
  windowDays: number;
  dateFrom?: string | null;
  dateTo?: string | null;
  region: string;
  tourId: string | null;
  onlyAttention: boolean;
  basis: CostBasis;
  confidenceTier?: Array<ConfidenceTier>;
  action?: CommandFlag | 'all';
  marginBand?: string;
}

/** The departure-date window the filters select, as inclusive YYYY-MM-DD bounds. */
export function windowRange(filters: CommandFilters, today: Date): { from: string; to: string } {
  const calendarDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const end = new Date(today);
  end.setDate(end.getDate() + filters.windowDays);
  return { from: filters.dateFrom || calendarDate(today), to: filters.dateTo || calendarDate(end) };
}

export function filtered(
  data: Array<CommandDeparture>,
  filters: CommandFilters,
  today: Date,
  ignoreRegion = false,
  ignoreWindow = false,
): Array<CommandDeparture> {
  const { from, to } = windowRange(filters, today);
  return data.filter(
    (d) =>
      (!filters.inReview || inReview(d)) &&
      (!filters.reviewStatus ||
        filters.reviewStatus === 'all' ||
        statusOf(d, filters.basis) === filters.reviewStatus) &&
      (!filters.rmReview || ['Urgent', 'Needs Action', 'Assessment Incomplete'].includes(statusOf(d, filters.basis))) &&
      (!filters.sellOut || !!d.sell_out_watch) &&
      (!filters.queue || queueCodes[filters.queue]?.includes(reviewOf(d, filters.basis)?.primary_intervention?.code)) &&
      (ignoreWindow || (d.dep >= from && d.dep <= to)) &&
      (ignoreRegion || filters.region === 'All' || d.region === filters.region) &&
      (!filters.tourId || d.opp_id === filters.tourId) &&
      (!filters.action || filters.action === 'all' || actionFlag(d, filters.basis) === filters.action) &&
      (!filters.confidenceTier ||
        filters.confidenceTier.length === 0 ||
        filters.confidenceTier.includes(d.pred_confidence_tier || 'indicative')),
  );
}

export const needsAttention = (tour: ReturnType<typeof agg>) =>
  tour.red + tour.amber + tour.empty + tour.loss_today + tour.predicted_loss > 0;

export function sydneyMonths(now: Date): Array<string> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  return Array.from({ length: 12 }, (_, index) => {
    const date = new Date(Date.UTC(year, month - 1 + index, 1));
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

export const pct = (v: number | null | undefined) =>
  v == null || Number.isNaN(v) ? '—' : `${v < -0.005 ? '−' : ''}${Math.abs(v * 100).toFixed(0)}%`;
export const money = (v: number | null | undefined) =>
  v == null ? '—' : `${v < 0 ? '−$' : '$'}${Math.abs(Math.round(v)).toLocaleString('en-AU')}`;

/** Short money for cards and chart labels: $950, $12k, $1.2m. */
export const moneyShort = (v: number | null | undefined) => {
  if (v == null || Number.isNaN(v)) {
    return '—';
  }
  const a = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (a >= 1e6) {
    return `${sign}$${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)}m`;
  }
  if (a >= 1e4) {
    return `${sign}$${Math.round(a / 1e3)}k`;
  }
  if (a >= 1e3) {
    return `${sign}$${(a / 1e3).toFixed(1)}k`;
  }
  return `${sign}$${Math.round(a)}`;
};

/** Margin rate tone, on the same thresholds as the flags: below 0% red, 0–10% amber, 10%+ green. */
export const marginTone = (rate: number | null | undefined): 'red' | 'amber' | 'green' | null =>
  rate == null || Number.isNaN(rate) ? null : rate < 0 ? 'red' : rate < 0.1 ? 'amber' : 'green';

export type PeriodUnit = 'month' | 'week';

const isoDay = (date: Date) => date.toISOString().slice(0, 10);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** First day of the departure month, or the Monday of the departure week. */
export function periodStart(dep: string, unit: PeriodUnit): string {
  if (unit === 'month') {
    return `${dep.slice(0, 7)}-01`;
  }
  const date = new Date(`${dep.slice(0, 10)}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return isoDay(date);
}

function nextPeriod(start: string, unit: PeriodUnit): string {
  const date = new Date(`${start}T00:00:00Z`);
  if (unit === 'month') {
    date.setUTCMonth(date.getUTCMonth() + 1);
  } else {
    date.setUTCDate(date.getUTCDate() + 7);
  }
  return isoDay(date);
}

export interface PeriodPoint extends ReturnType<typeof agg> {
  key: string;
  label: string;
  /** Inclusive departure dates covered by the period. */
  from: string;
  to: string;
  departures?: Array<CommandDeparture>;
}

/**
 * Real and predicted position by departure month or week across the selected window. Every period in the
 * window is returned, including ones with no departures, so the bars line up with the calendar.
 */
export function periods(
  rows: Array<CommandDeparture>,
  basis: CostBasis,
  unit: PeriodUnit,
  range: { from: string; to: string },
): Array<PeriodPoint> {
  const by: Record<string, Array<CommandDeparture>> = {};
  rows.forEach((d) => {
    (by[periodStart(d.dep, unit)] ||= []).push(d);
  });
  const result: Array<PeriodPoint> = [];
  for (let key = periodStart(range.from, unit); key <= range.to && result.length < 120; key = nextPeriod(key, unit)) {
    const last = new Date(`${nextPeriod(key, unit)}T00:00:00Z`);
    last.setUTCDate(last.getUTCDate() - 1);
    const date = new Date(`${key}T00:00:00Z`);
    result.push({
      ...agg(by[key] || [], basis),
      departures: by[key] || [],
      key,
      label:
        unit === 'month'
          ? `${MONTHS[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(2)}`
          : `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`,
      from: key < range.from ? range.from : key,
      to: isoDay(last) > range.to ? range.to : isoDay(last),
    });
  }
  return result;
}

export interface HeatCell {
  month: string;
  lf: number | null;
  marginRate: number | null;
  red: boolean;
  n: number;
  pax: number;
  cap: number;
}
export interface TourRow extends ReturnType<typeof agg> {
  marginDeparture?: CommandDeparture;
  id: string;
  name: string;
  region: string;
  country: string | null;
  ltle: boolean;
  deps: number;
  rec: string;
  recPredictionBased: boolean;
  recDeparture?: CommandDeparture;
  hm: Array<HeatCell>;
  rows: Array<CommandDeparture>;
}

export function sortTourRows(rows: Array<TourRow>, key: keyof TourRow, direction: number): Array<TourRow> {
  return [...rows].sort((x, y) => {
    const a = x[key] ?? -1e9;
    const b = y[key] ?? -1e9;
    return (
      (typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b) : Number(a) - Number(b)) * direction ||
      y.red - x.red
    );
  });
}

export function sortToursByUrgency(rows: Array<TourRow>): Array<TourRow> {
  return [...rows].sort(
    (a, b) => b.red - a.red || b.amber - a.amber || b.empty - a.empty || b.ttv - a.ttv || a.name.localeCompare(b.name),
  );
}

export function tourRows(
  rows: Array<CommandDeparture>,
  heatmapData: Array<CommandDeparture>,
  basis: CostBasis,
  months: Array<string>,
): Array<TourRow> {
  const by: Record<string, Array<CommandDeparture>> = {};
  rows.forEach((d) => {
    (by[d.opp_id] ||= []).push(d);
  });
  return Object.entries(by).map(([id, group]) => {
    const a = agg(group, basis);
    // Only eligible short-lead predictions can contribute to marketing/LTLE advice.
    const eligible = group.filter(
      (d) =>
        d.days_to_handback >= 0 &&
        d.days_to_handback <= 240 &&
        ['watch', 'needs_action'].includes(d[`suggestion_code_${basis}`] || d.suggestion_code || 'show_prediction'),
    );
    const red = eligible.filter((d) => basisValue(d, 'flag', basis) === 'red');
    const amber = eligible.filter((d) => basisValue(d, 'flag', basis) === 'amber');
    let rec = '';
    let recDeparture: CommandDeparture | undefined;
    const empty = group.filter((d) => basisValue(d, 'flag', basis) === 'empty').length;
    if (red.length + amber.length >= 3 && !group[0].ltle) {
      rec = `Generate an LTLE for this tour (${red.length + amber.length} dates need help)`;
    } else if (red.length + amber.length >= 3) {
      rec = `Push this LTLE (marketing) or reduce price (${red.length + amber.length} dates need help)`;
    }
    const openable = group.filter((d) => d.state === 'Closed (not on sale)' && d.days_to_handback >= 30).length;
    if (empty >= 3) {
      rec = `${empty} on-sale dates with no bookings near handback: close or hand back${rec ? `; or ${rec.charAt(0).toLowerCase()}${rec.slice(1)}` : ''}`;
    }
    if (
      !rec &&
      group.some((d) => d.days_to_handback > 0 && d.days_to_handback <= 240) &&
      a.lf != null &&
      a.lf >= 0.8 &&
      openable
    ) {
      rec = `Selling well: open some of the ${openable} closed dates (handback 30+ days away)`;
    }
    if (!rec) {
      const urgent = [...group]
        .filter((d) => d.days_to_handback >= 0 && !!basisValue(d, 'rec', basis))
        .sort((x, y) => {
          const rank = (d: CommandDeparture) =>
            ({ red: 0, amber: 1, empty: 2 })[String(basisValue(d, 'flag', basis))] ?? 3;
          return rank(x) - rank(y) || x.days_to_handback - y.days_to_handback;
        })[0];
      if (urgent) {
        recDeparture = urgent;
        const suggestion = String(
          basisValue(urgent, 'rec', basis) || flagLabels[basisValue(urgent, 'flag', basis) as CommandFlag],
        );
        const date = new Date(`${urgent.dep}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
        rec = `${date}: ${suggestion}`;
      }
    }
    if (!rec) {
      const ranks = { show_prediction: 0, early_watch: 1, watch: 2, needs_action: 3 };
      const highest = group
        .filter((d) => d.days_to_handback >= 0)
        .sort(
          (x, y) =>
            ranks[y[`suggestion_code_${basis}`] || y.suggestion_code || 'show_prediction'] -
              ranks[x[`suggestion_code_${basis}`] || x.suggestion_code || 'show_prediction'] ||
            x.days_to_handback - y.days_to_handback,
        )[0];
      if (highest && (highest[`suggestion_code_${basis}`] || highest.suggestion_code) !== 'show_prediction') {
        rec = highest[`suggestion_label_${basis}`] || highest.suggestion_label || '';
        recDeparture = highest;
      }
    }
    const hm = months.map((month) => {
      const x = heatmapData.filter((d) => d.opp_id === id && d.dep.slice(0, 7) === month);
      const c = x.filter(counted);
      const cap = c.reduce((s, d) => s + (d.max_pax || 0), 0);
      const pax = c.reduce((s, d) => s + d.pax, 0);
      const predTtv = x.reduce((s, d) => s + (d.pred_ttv || 0), 0);
      const predMargin = x.reduce((s, d) => s + (d[`pred_margin_${basis}`] || 0), 0);
      return {
        month,
        lf: cap ? pax / cap : null,
        marginRate: predTtv ? predMargin / predTtv : null,
        red: x.some((d) => basisValue(d, 'flag', basis) === 'red'),
        n: x.length,
        pax,
        cap,
      };
    });
    return {
      id,
      name: group[0].opp_name,
      region: group[0].region,
      country: group[0].country,
      ltle: group[0].ltle,
      deps: group.length,
      ...a,
      marginDeparture: worstMarginDeparture(group, basis),
      rec,
      recPredictionBased: recommendationIsPredictionBased(rec),
      recDeparture,
      hm,
      rows: group,
    };
  });
}

export const flagLabels: Record<CommandFlag, string> = {
  red: 'Needs Action',
  amber: 'Watch',
  green: 'On Track',
  early: 'No Bookings Yet',
  closed: 'Closed',
  'handed back': 'Past Handback',
  'ltle copy': 'LTLE Copy',
  unavailable: 'Margin Unavailable',
  empty: 'No Bookings',
};

export type ChartUnit = PeriodUnit | 'departure';

/** Remaining seats are deliberately not clamped: a negative value exposes overbooking. */
export const seatsLeft = (capacity: number | null, travellers: number | null | undefined): number | null =>
  capacity == null || travellers == null ? null : Math.round(capacity - travellers);

export interface DeparturePoint extends PeriodPoint {
  departure: CommandDeparture;
  extra: number;
  capacity: number | null;
  breakeven: number | null;
  flag: CommandFlag;
  isFinal: boolean;
}

/** One point per displayed departure, including closed dates and duplicate dates. */
export function departureSeries(rows: Array<CommandDeparture>, basis: CostBasis): Array<DeparturePoint> {
  return [...rows]
    .sort((a, b) => a.dep.localeCompare(b.dep) || a.dep_id.localeCompare(b.dep_id))
    .map((d) => ({
      ...agg([d], basis),
      key: d.dep_id,
      label: `${Number(d.dep.slice(8, 10))} ${MONTHS[Number(d.dep.slice(5, 7)) - 1]} ${d.dep.slice(2, 4)}`,
      from: d.dep,
      to: d.dep,
      departure: d,
      extra: Math.max(0, (d.pred_pax ?? 0) - d.pax),
      capacity: d.max_pax,
      breakeven: d[`breakeven_pax_${basis}`] ?? null,
      flag: basisValue(d, 'flag', basis) as CommandFlag,
      isFinal: d.days_to_handback <= 0,
    }));
}

export const parseChartTour = (search: string): string | null => new URLSearchParams(search).get('tour') || null;

export function serialiseChartTour(search: string, tour: string | null): string {
  const params = new URLSearchParams(search);
  if (tour) {
    params.set('tour', tour);
  } else {
    params.delete('tour');
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Scope options and validity come from the visible tour list, not all snapshot tours. */
export const chartScope = (tour: string | null, tours: Array<Pick<TourRow, 'id'>>): string | null =>
  tour && tours.some((t) => t.id === tour) ? tour : null;

// Include zero and keep 10% of the plotted range above the tallest margin bar.
function marginStep(values: Array<number>): number {
  const low = Math.min(0, ...values.filter(Number.isFinite));
  const high = Math.max(0, ...values.filter(Number.isFinite));
  const rough = Math.max(high - low, 1) / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  return ([1, 2, 5, 10].find((step) => step * power >= rough) ?? 10) * power;
}

export function marginDomain(values: Array<number>): [number, number] {
  const step = marginStep(values);
  const low = Math.min(0, ...values.filter(Number.isFinite));
  const high = Math.max(0, ...values.filter(Number.isFinite));
  return [
    Math.floor(low / step) * step,
    Math.max(step, Math.ceil((high + Math.max(high - low, 1) * 0.1) / step) * step),
  ];
}

export function marginTicks(values: Array<number>): Array<number> {
  const [low, high] = marginDomain(values);
  const step = marginStep(values);
  return Array.from({ length: Math.round((high - low) / step) + 1 }, (_, index) => low + index * step);
}

export const travellerCount = (value: number | null) =>
  value == null
    ? '—'
    : `${Math.round(value).toLocaleString('en-AU')} ${Math.round(value) === 1 ? 'traveller' : 'travellers'}`;

export const showPredictedLabel = (booked: number, predicted: number) => booked !== predicted;

/** Dates prefixed by tour summaries do not change the origin of the advice. */
export function recommendationIsPredictionBased(text: string): boolean {
  const advice = text.replace(/^\d{1,2} [A-Za-z]{3}: /, '');
  return (
    !!advice &&
    !/^(No bookings,|Handback in ≤14 days:|Selling well:|Increase price:|Loses money at every group size:|Flagged on predicted margin;|\d+ on-sale dates with no bookings)/.test(
      advice,
    )
  );
}

/** Keep booked facts and prediction values, but remove actions based on a stale forecast. */
export function stalePredictionRows(rows: Array<CommandDeparture>): Array<CommandDeparture> {
  const keepFact = (text: string | undefined) =>
    text && !recommendationIsPredictionBased(text) && !text.startsWith('Flagged on predicted margin;') ? text : '';
  return rows.map((row) => ({
    ...row,
    pred_confidence_tier: 'indicative',
    flag_rep: row.flag_rep === 'red' ? 'amber' : row.flag_rep,
    flag_ss: row.flag_ss === 'red' ? 'amber' : row.flag_ss,
    suggestion_code: 'show_prediction',
    suggestion_code_rep: 'show_prediction',
    suggestion_code_ss: 'show_prediction',
    suggestion_label: keepFact(row.suggestion_label),
    suggestion_label_rep: keepFact(row.suggestion_label_rep),
    suggestion_label_ss: keepFact(row.suggestion_label_ss),
    rec_rep: keepFact(row.rec_rep),
    rec_ss: keepFact(row.rec_ss),
  }));
}

export function departureSeverity(d: CommandDeparture, basis: CostBasis): Severity {
  const review = statusOf(d, basis);
  if (review) {
    return ['Urgent', 'Needs Action'].includes(review)
      ? d[`pred_margin_${basis}`] != null && d[`pred_margin_${basis}`] < 0
        ? 'predicted_loss'
        : 'other_action'
      : review === 'Watch'
        ? 'watch'
        : 'none';
  }
  if (d.days_to_handback < 0 || !counted(d)) {
    return 'none';
  }
  if (actionFlag(d, basis) === 'red' && d[`margin_bands_${basis}`]?.pred_margin_pct_p50 < 0) {
    return 'predicted_loss';
  }
  if (basisValue(d, 'flag', basis) === 'red') {
    return 'other_action';
  }
  if (basisValue(d, 'flag', basis) === 'amber') {
    return 'watch';
  }
  return 'none';
}
export const departureOnTrack = (d: CommandDeparture, basis: CostBasis) =>
  statusOf(d, basis)
    ? statusOf(d, basis) === 'On Track'
    : counted(d) && d.days_to_handback >= 0 && actionFlag(d, basis) === 'green';

export const severityLabels: Record<Severity, string> = {
  loss_today: 'Loss today',
  predicted_loss: 'Predicted loss',
  other_action: 'Other needs action',
  watch: 'Watch',
  none: 'None',
};
